/* Copyright (c) 2020-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { theme } from "@nordcloud/gnui";
import { isNil } from "~/tools";
import { Bars, ChartContainer } from "../components";
import { DataItem } from "../types";
import { getXScale, getYScale } from "../utils";
import { ReservationUtilizationTimePeriod } from "./types";
import {
  generateMonthlyChartTicks,
  generateOneMonthTicks,
  generateThreeMonthsTicks,
} from "./utils";

const VERTICAL_MARGIN = 40;

const barsColorTheme = {
  gray: [theme.colors.darks[4], theme.colors.darks[3]],
  normal: [theme.colors.success, theme.colors.danger],
} as const;

type Props<T> = {
  width: number;
  height: number;
  data: T[];
  loading?: boolean;
  period?: ReservationUtilizationTimePeriod;
};

export function MonthlyUtilisationChart<T extends DataItem>({
  width,
  height,
  data = [],
  loading = true,
  period,
}: Props<T>) {
  const xMax = width - 30;
  const yMax = height - VERTICAL_MARGIN;
  const BARS_NUMBER = dayjs().daysInMonth();
  const numTicks = data.length;
  const utilisationData = new Array(numTicks).fill(0);
  const benefitData = new Array(numTicks).fill(0);
  const xScale = getXScale(xMax, utilisationData);
  const yScale = getYScale(yMax);
  const barWidth = Number(xMax) / (numTicks * 1.5);

  data.forEach((item, index) => {
    utilisationData.splice(index, 1, Number(item?.value) * 100 || 0);
    benefitData.splice(index, 1, Number(item?.benefit) || 0);
  });

  const ticks = React.useMemo(() => {
    if (isNil(period)) {
      return generateMonthlyChartTicks(data);
    }

    if (period === ReservationUtilizationTimePeriod.OneMonth) {
      return generateOneMonthTicks(data, BARS_NUMBER);
    }

    return generateThreeMonthsTicks(data, period);
  }, [period, data, BARS_NUMBER]);

  return (
    <ChartContainer
      data={data}
      loading={loading}
      width={width}
      height={height}
      yTicks={10}
      numTicks={numTicks}
      offset={30}
      xTickFormat={(number: number) => ticks[number]}
    >
      <Bars
        data={utilisationData}
        yScale={yScale}
        xScale={xScale}
        yMax={yMax}
        benefit={benefitData}
        barWidth={barWidth}
      />
    </ChartContainer>
  );
}

export const monthlyUtilizationChartLegend = [
  {
    color: barsColorTheme.normal[1],
    opacity: 0.6,
    label: "underutilized",
    hasTooltip: true,
    caption: "Total cost on-demand equivalent - total reservation cost < 0",
  },
  {
    color: barsColorTheme.normal[0],
    opacity: 0.6,
    label: "utilized",
    hasTooltip: true,
    caption: "Total cost on-demand equivalent - total reservation cost >= 0",
  },
];
